/**
 * Sentinel findings and the run report, stored after they are asked for.
 *
 * Live keeps the conversation. This tab is where a draft becomes a finding
 * and where the write-up lives after the run, so opening chat no longer
 * buries the report under the thread.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import {
  Mission,
  MissionAfterAction,
  MissionInsight,
  downloadRunReport,
  draftAfterAction,
  draftMissionInsights,
  listMissionInsights,
  validateMissionInsight,
} from '../../services/missionApi';

import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToyOutlined';

const GOLD = '#f5b942';
const RED = '#ef5350';
const GREEN = '#66bb6a';

const Action: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  accent?: boolean;
  tone?: string;
  children: React.ReactNode;
}> = ({ onClick, disabled, accent, tone, children }) => (
  <Typography
    variant="caption"
    role="button"
    tabIndex={disabled ? -1 : 0}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(e) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
    }}
    sx={{
      fontWeight: 700,
      whiteSpace: 'nowrap',
      cursor: disabled ? 'default' : 'pointer',
      color: disabled ? 'text.disabled' : tone || (accent ? GOLD : 'text.secondary'),
      '&:hover': disabled ? undefined : { textDecoration: 'underline' },
    }}
  >
    {children}
  </Typography>
);

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export interface MissionReportTabProps {
  mission: Mission;
  onError: (message: string | null) => void;
  onMissionChange: () => void;
  refreshKey?: number;
  onCount?: (count: number) => void;
}

const MissionReportTab: React.FC<MissionReportTabProps> = ({
  mission, onError, onMissionChange, refreshKey, onCount,
}) => {
  const [insights, setInsights] = useState<MissionInsight[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [report, setReport] = useState<MissionAfterAction | null>(null);

  const errorRef = React.useRef(onError);
  errorRef.current = onError;
  const countRef = React.useRef(onCount);
  countRef.current = onCount;

  const refresh = useCallback(async () => {
    try {
      const rows = await listMissionInsights(mission.id);
      setInsights(rows);
      countRef.current?.(rows.filter((row) => row.validation !== 'rejected').length);
    } catch {
      setInsights([]);
      countRef.current?.(0);
    }
  }, [mission.id]);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  const guard = useCallback(async (key: string, work: () => Promise<void>) => {
    setBusy(key);
    errorRef.current(null);
    try {
      await work();
    } catch (e: any) {
      errorRef.current(e?.response?.data?.detail || e?.message || 'Sentinel could not do that');
    } finally {
      setBusy(null);
    }
  }, []);

  const drafts = insights.filter((i) => i.validation === 'draft');
  const accepted = insights.filter((i) => i.validation === 'accepted');
  const rejected = insights.filter((i) => i.validation === 'rejected');

  return (
    <Box>
      <DockStatStrip
        items={[
          { label: 'Findings', value: drafts.length + accepted.length, color: '#e8ebee', icon: <DescriptionIcon /> },
          { label: 'Drafts', value: drafts.length, color: GOLD, icon: <EditIcon /> },
          { label: 'Accepted', value: accepted.length, color: GREEN, icon: <CheckCircleIcon /> },
          { label: 'Rejected', value: rejected.length, color: RED },
          { label: 'AI', value: insights.filter((i) => i.origin === 'ai').length, color: '#42a5f5', icon: <SmartToyIcon /> },
          { label: 'Analyst', value: insights.filter((i) => i.origin === 'analyst').length, color: '#ce93d8' },
          { label: 'Categories', value: new Set(insights.filter((i) => i.validation !== 'rejected').map((i) => i.category || 'general')).size, color: '#ab47bc' },
          { label: 'Write-up', value: report ? 1 : 0, color: report ? GREEN : '#8a939f' },
        ]}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.35 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
          REPORT
        </Typography>
        <DockTabHelp tab="report" />
        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
          drafts only, until you accept them
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 0.75 }}>
        <Action
          onClick={() => guard('insights', async () => {
            await draftMissionInsights(mission.id);
            await refresh();
          })}
          disabled={!!busy}
        >
          {busy === 'insights' ? 'Reading…' : 'Draft findings'}
        </Action>
        <Action
          onClick={() => guard('report', async () => {
            const { draft } = await draftAfterAction(mission.id);
            setReport(draft);
          })}
          disabled={!!busy}
        >
          {busy === 'report' ? 'Writing…' : 'Run report'}
        </Action>
        <Action
          onClick={() => guard('export', async () => {
            saveBlob(await downloadRunReport(mission.id), `mission-${mission.id}-run.html`);
          })}
          disabled={!!busy}
        >
          Export
        </Action>
      </Box>

      {insights.length === 0 && !report && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.63rem' }}>
          Nothing stored yet. Draft findings or run the report and they stay here.
        </Typography>
      )}

      {drafts.length > 0 && (
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.15 }}>
            {drafts.length} draft{drafts.length === 1 ? '' : 's'} for review
          </Typography>
          {drafts.map((insight) => (
            <Box key={insight.id} sx={{ py: 0.3 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, minWidth: 0, fontSize: '0.67rem' }}>
                  {insight.title}
                </Typography>
                <Action
                  tone={GREEN}
                  onClick={() => guard(`v${insight.id}`, async () => {
                    await validateMissionInsight(mission.id, insight.id, 'accepted');
                    await refresh();
                  })}
                  disabled={!!busy}
                >
                  Accept
                </Action>
                <Action
                  tone={RED}
                  onClick={() => guard(`v${insight.id}`, async () => {
                    await validateMissionInsight(mission.id, insight.id, 'rejected');
                    await refresh();
                  })}
                  disabled={!!busy}
                >
                  Reject
                </Action>
              </Box>
              <Typography
                variant="caption"
                sx={{ display: 'block', fontSize: '0.63rem', color: 'text.secondary', lineHeight: 1.4 }}
              >
                {insight.finding}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {accepted.length > 0 && (
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.63rem', color: GREEN, mb: 0.25 }}>
            {accepted.length} accepted finding{accepted.length === 1 ? '' : 's'} will appear in the report.
          </Typography>
          {accepted.map((insight) => (
            <Box key={insight.id} sx={{ py: 0.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.67rem' }}>
                {insight.title}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.63rem', color: 'text.secondary' }}>
                {insight.finding}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!report} onClose={() => setReport(null)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>
            RUN REPORT · DRAFT
          </Typography>
          {report && (['executive_summary', 'key_findings', 'pol_assessment', 'gaps'] as const).map((key) => (
            report[key] ? (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textTransform: 'capitalize' }}>
                  {key.replace(/_/g, ' ')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {report[key]}
                </Typography>
              </Box>
            ) : null
          ))}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 1.5 }}>
            <Action onClick={() => setReport(null)}>Discard</Action>
            <Action
              accent
              disabled={!!busy}
              onClick={() => guard('publish', async () => {
                await draftAfterAction(mission.id, { publish: true });
                setReport(null);
                onMissionChange();
                await refresh();
              })}
            >
              Publish to the mission
            </Action>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MissionReportTab;
